import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    Injector,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {SafeHtml} from '@angular/platform-browser';
import {L10nTranslationService} from 'angular-l10n';
import moment from 'moment';
import {merge, Observable} from 'rxjs';
import {map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {IS_ORDER_REPLACE_DISABLED} from '../../common/constants';
import {TypedForm, TypedFormOwnControls} from '../../common/form-types';
import {setControlEnabledState} from '../../common/form-utils';
import {formatDateTime} from '../../common/formatters';
import {BehaviorSubjectSkipSame} from '../../common/rxjs/behavior-subject-skip-same';
import {AccountPermission} from '../../components/account-permissions';
import {AccountService} from '../../components/accounts';
import {destroy$} from '../../components/destroyable';
import {InvPortfolioService} from '../../components/investment-portfolio';
import {Widget} from '../../components/layout/widget/widget';
import {ModalService} from '../../components/modal';
import {getOrderQtyAssetType, getQtyFromOrder} from '../../components/order-qty-multi-currency';
import {SecurityService} from '../../components/security';
import {getOrderAspreaderAlgoParamsFormFields, isAsecurityKey} from '../../components/security/aspreader-security';
import {SelectItem} from '../../components/select-input/select-item';
import {isSmartSecurityKey} from '../../components/smart-security';
import {DisplayDecimal, formatReport, SymbolDecimalsService, SymbolFormats} from '../../components/symbol-decimals';
import {AllOrNone} from '../../enums/all-or-none';
import {Exchange} from '../../enums/exchange';
import {
    getNearestSniperDelayUnit,
    getNearestTwapStepDelayUnit,
    ORDER_ALGO_TYPE_MENU,
    OrderAlgoType,
    parseAlgoTypeFromOrder
} from '../../enums/order-algo-type';
import {OrderStatus} from '../../enums/order-status';
import {OrderTransactionType} from '../../enums/order-transaction-type';
import {OrdType} from '../../enums/order-type';
import {Side} from '../../enums/side';
import {ExchangeAccount, ExchangeAccountBase} from '../../models/exchange-account';
import {Order, ReplaceOrCancelOrder} from '../../models/order';
import {OrderSniperAlgoParams, OrderTwapAlgoParams} from '../../models/order-algo-params';
import {Security} from '../../models/security';
import {WidgetCommonAction} from '../../pages/dashboard/layout/widget';
import {HtmlSymbolService} from '../../services/html-symbol.service';
import {OrderFormValue} from '../components/float-widget-common/order-modal-form-block';
import {OrderPlacingService} from '../components/order-placing';
import {getAccountBaseByNewOrderParams} from '../components/order-placing/order-placing-utils';
import {WIDGET_ORDER_PLACING_PROVIDERS} from '../components/order-placing/widget-order-placing-providers';
import {bindControlsToSettings} from '../components/widget-impl-settings-component-base';
import {
    DisplayOrder,
    DisplayOrderFactory,
    ExecQtyWithMultiCurrency,
    ORDER_STATUS_INFO_DICT,
    OrderStatusInfo
} from '../orders-widget/display-order.factory';
import {OrderDetailsWidget} from './order-details-widget';

@Component({
    selector: 'order-details-widget',
    templateUrl: './order-details-widget.component.html',
    styleUrls: ['./order-details-widget.component.scss'],
    providers: [
        ...WIDGET_ORDER_PLACING_PROVIDERS,
        DisplayOrderFactory
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrderDetailsWidgetComponent {

    public readonly MDASH: SafeHtml = this.htmlSymbolService.mdash();

    public orderInfoExecPrice: DisplayDecimal;

    public accountBase: ExchangeAccountBase;

    public orderInfoCreatedOn: string | SafeHtml = this.MDASH;
    public orderInfoStatus: OrderStatusInfo;

    public readonly rejectionReason$ = this.getDisplayRejectionReasonStream();

    public security: Security;

    public readonly Exchange = Exchange;

    public execQtyWithMultiCurrency: ExecQtyWithMultiCurrency;

    public isActionsDisabled: boolean = true; // True by default in order to hide buttons while loading.
    public isOrderRejected: boolean = false;
    public isReplaceDisabled: boolean = false;

    public readonly destroy$ = destroy$();

    public orderLeavesQty: number;

    public isInvPortfoliosAvailable: boolean;

    public orderForm: TypedForm<OrderFormValue>;

    @ViewChild('confirmModalAnchor', {read: ViewContainerRef, static: true})
    public confirmModalAnchorViewContainerRef: ViewContainerRef;

    public maxShowTpl: TemplateRef<unknown>;
    public maxUnfilledTpl: TemplateRef<unknown>;

    public algoTypeMenu: Array<SelectItem<OrderAlgoType>> = ORDER_ALGO_TYPE_MENU;

    public readonly OrderAlgoType = OrderAlgoType;

    public readonly Side = Side;

    public readonly isOrderInited$ = new BehaviorSubjectSkipSame<boolean>(false);

    public isSmartSecurity: boolean = false;

    public isAsecurity: boolean = false;

    public displayOrder: DisplayOrder;

    constructor(
        @Inject(Widget)
        public widget: OrderDetailsWidget,
        protected orderPlacingService: OrderPlacingService,
        protected htmlSymbolService: HtmlSymbolService,
        protected modalService: ModalService,
        protected securityService: SecurityService,
        protected symbolDecimalsService: SymbolDecimalsService,
        protected accountService: AccountService,
        protected translationService: L10nTranslationService,
        protected displayOrderFactory: DisplayOrderFactory,
        protected invPortfolioService: InvPortfolioService,
        protected injector: Injector
    ) {
        this.buildOrderForm();
        this.initSubscriptions();
    }

    public goToConfirmation(transactionType: OrderTransactionType): void {
        const {hasWritePermission} = this.checkWritePermissionOnAccount();

        if (hasWritePermission) {
            this.submitOrder(transactionType);
        }
    }

    public submitOrder(transactionType: OrderTransactionType): void {
        const {confirmModalAnchorViewContainerRef, widget} = this;
        const {security, leverage, portfolioId, investmentEntityId} = widget.implementationInstance.order;
        const orderFormValue = this.orderForm.getRawValue();

        const accountBase = this.accountBase;

        const newOrder: ReplaceOrCancelOrder = this.orderPlacingService.getOrderForForm(
            security,
            accountBase,
            orderFormValue,
            transactionType,
            investmentEntityId,
            portfolioId,
            leverage
        ) as ReplaceOrCancelOrder;

        this.orderPlacingService.initiateOrderPlacing(
            {
                security,
                accountBase,
                confirmModalAnchorVcRef:  confirmModalAnchorViewContainerRef,
                isConfirmationDisabled:   false,
                isOneClickTradingEnabled: true,
                isOrderDetails:           true
            },
            newOrder
        ).pipe(
            tap(() => this.close()),
            takeUntil(this.destroy$)
        ).subscribe({
            error: () => null // Hide any possible exceptions. Just close the modal for now.
        });
    }

    public close(): void {
        this.widget.action$.next({
            type: WidgetCommonAction.REMOVE_WIDGET
        });
    }

    protected initSubscriptions(): void {
        const updateSecurity = () => {
            const securityKey = this.widget.implementationInstance.settings.securityKey;

            return this.securityService.getSecurityBySecurityKey(securityKey).pipe(
                tap(security => this.security = security),
                tap(() => this.updateSecurityFlags())
            );
        };

        const security$ = this.widget.implementationInstance.getSettingsSecurityKeyStream().pipe(
            switchMap(updateSecurity)
        );

        const order$ = this.listenToOrder();

        merge(
            security$,
            order$.pipe(

                // Handle the case, when security is available from order only.
                // It's already saved from order to cache.
                switchMap(updateSecurity)
            )
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    // tslint:disable:cyclomatic-complexity
    protected updateOrder(order: Order, isActionsDisabled?: boolean): void {
        const {security} = order;
        this.security = security;

        const symbolFormats: SymbolFormats = this.symbolDecimalsService.getSecurityFormats(security);
        const orderStatus: OrderStatus = order.state.orderStatus;

        this.isActionsDisabled = isActionsDisabled
            || orderStatus === OrderStatus.REJECTED
            || orderStatus === OrderStatus.FILLED
            || orderStatus === OrderStatus.EXPIRED
            || orderStatus === OrderStatus.CANCEL
            || orderStatus === OrderStatus.CANCELED;

        const displayOrder = this.displayOrderFactory.createDisplayOrder(order);
        this.displayOrder = displayOrder;

        this.execQtyWithMultiCurrency = displayOrder.execQtyWithMultiCurrency;

        this.orderLeavesQty = orderStatus === OrderStatus.PARTIALLY_FILLED ? order.state.leavesQty : null;

        this.orderInfoCreatedOn = order.transactTime ? formatDateTime(order.transactTime) : this.MDASH;
        this.orderInfoExecPrice = formatReport(order.state.avgPx, symbolFormats);
        this.orderInfoStatus = ORDER_STATUS_INFO_DICT[orderStatus];

        this.isOrderRejected = orderStatus === OrderStatus.REJECTED;

        const algoType = parseAlgoTypeFromOrder(order.algoType);

        /**
         * [ECN-135] Disable replacing of Stop Limit orders
         */
        this.isReplaceDisabled = this.isActionsDisabled
            || order.ordType === OrdType.STOP_LIMIT
            || algoType === OrderAlgoType.SNIPER
            || IS_ORDER_REPLACE_DISABLED;

        const accountBase = getAccountBaseByNewOrderParams(order);
        this.accountBase = accountBase;

        this.isInvPortfoliosAvailable = !!order.investmentEntityId || !!order.portfolioId;

        this.updateFormByOrder(accountBase);
    }

    private updateFormByOrder(accountBase: ExchangeAccountBase): void {
        const order = this.widget.implementationInstance.order;
        const {security} = order;

        const orderFormValueUpdate: Partial<OrderFormValue> = {
            algoType:           parseAlgoTypeFromOrder(order.algoType),
            accountBase,
            quantity:           getQtyFromOrder(security, order),
            side:               order.side,
            orderType:          order.ordType,
            limitPrice:         order.price,
            stopPrice:          order.stopPx,
            allOrNone:          order.allOrNone || AllOrNone.NO, // We don't get value for this field from server
            timeInForce:        order.timeInForce,
            origClOrdId:        order.clOrdId,
            qtyAssetType:       getOrderQtyAssetType(security, order),
            investmentEntityId: order.investmentEntityId,
            portfolioId:        order.portfolioId,
            isIcebergEnabled:   !!order.icebergQty,
            icebergQty:         order.icebergQty,
            ...this.getOrderTwapAlgoParamsFormFields(order),
            ...this.getOrderSniperAlgoParamsFormFields(order),
            ...getOrderAspreaderAlgoParamsFormFields(order, this.accountService)
        };

        // TODO: Apply also stored values, skip same values, check, when support Replace
        this.orderForm.patchValue(orderFormValueUpdate);

        setControlEnabledState(this.orderForm, !this.isReplaceDisabled);
        this.isOrderInited$.next(true);
    }

    private listenToOrder(): Observable<any> {
        return this.widget.implementationInstance.order$.pipe(
            tap(order => this.updateOrder(order))
        );
    }

    private getDisplayRejectionReasonString(): string {
        const order = this.widget.implementationInstance.order;

        return this.displayOrderFactory.getDisplayRejectionReasonString(order);
    }

    private getDisplayRejectionReasonStream(): Observable<string | SafeHtml> {
        return merge(
            this.widget.implementationInstance.order$,
            this.translationService.onChange()
        ).pipe(
            map(() => this.getDisplayRejectionReasonString() || this.MDASH)
        );
    }

    private checkWritePermissionOnAccount(): {accountById: ExchangeAccount, hasWritePermission: boolean} {
        const accountById = this.accountService.getAccountByIdFromCache(this.accountBase.id);
        const hasWritePermission = accountById && accountById.permission === AccountPermission.WRITE;

        return {
            accountById,
            hasWritePermission
        };
    }

    private buildOrderForm(): void {
        const widget = this.widget;

        const controls: TypedFormOwnControls<OrderFormValue> = {
            algoType:           new FormControl({value: null, disabled: true}),
            accountBase:        new FormControl({value: null, disabled: true}),
            quantity:           new FormControl(),
            side:               new FormControl({value: null, disabled: true}),
            orderType:          new FormControl({value: null, disabled: true}),
            limitPrice:         new FormControl(),
            stopPrice:          new FormControl(),
            allOrNone:          new FormControl({value: null, disabled: true}),
            timeInForce:        new FormControl({value: null, disabled: true}),
            origClOrdId:        new FormControl({value: null, disabled: true}),
            qtyAssetType:       new FormControl(),
            investmentEntityId: new FormControl({value: null, disabled: true}),
            portfolioId:        new FormControl({value: null, disabled: true}),
            buyLeverage:        new FormControl({value: null, disabled: true}),
            sellLeverage:       new FormControl({value: null, disabled: true}),
            isIcebergEnabled:   new FormControl({value: null, disabled: true}),
            icebergQty:         new FormControl({value: null, disabled: true}),
            twapTimeRange:      new FormControl(),
            twapStepDelay:      new FormControl(),
            twapRandomQty:      new FormControl(),
            twapRandomTime:     new FormControl(),
            twapStepDelayUnit:  new FormControl(),
            sniperDelay:        new FormControl(),
            sniperDelayUnit:    new FormControl(),
            legsAccountDict:    new FormControl({value: null, disabled: true})
        };

        const bindings = [
            bindControlsToSettings<OrderDetailsWidget>({
                widget,
                dict: {
                    clOrdId:           controls.origClOrdId,
                    quantity:          controls.quantity,
                    limitPrice:        controls.limitPrice,
                    stopPrice:         controls.stopPrice,
                    qtyAssetType:      controls.qtyAssetType,
                    twapTimeRange:     controls.twapTimeRange,
                    twapStepDelay:     controls.twapStepDelay,
                    twapRandomQty:     controls.twapRandomQty,
                    twapRandomTime:    controls.twapRandomTime,
                    twapStepDelayUnit: controls.twapStepDelayUnit,
                    sniperDelay:       controls.sniperDelay,
                    sniperDelayUnit:   controls.sniperDelayUnit
                }
            })
        ];

        this.orderForm = new FormGroup(controls) as TypedForm<OrderFormValue>;

        merge(...bindings).pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private getOrderTwapAlgoParamsFormFields(order: Order): Partial<OrderFormValue> {
        const algoParams = order.algoParams as OrderTwapAlgoParams;
        let result: Partial<OrderFormValue>;

        if (algoParams && algoParams.algoType === OrderAlgoType.TWAP) {
            let twapStepDelay: number = algoParams.twapStepDelay;
            const twapStepDelayUnit: number = twapStepDelay && getNearestTwapStepDelayUnit(twapStepDelay);

            if (twapStepDelayUnit) {
                twapStepDelay = twapStepDelay / twapStepDelayUnit;
            }

            result = {
                twapTimeRange:  [
                    algoParams.twapStartTime ? moment.utc(algoParams.twapStartTime).valueOf() : undefined,
                    algoParams.twapStartTime ? moment.utc(algoParams.twapEndTime).valueOf() : undefined
                ],
                twapStepDelay,
                twapRandomQty:  algoParams.twapRandomQty,
                twapRandomTime: algoParams.twapRandomTime,
                twapStepDelayUnit
            };
        }

        return result;
    }

    private getOrderSniperAlgoParamsFormFields(order: Order): Partial<OrderFormValue> {
        const algoParams = order.algoParams as OrderSniperAlgoParams;
        let result: Partial<OrderFormValue>;

        if (algoParams && algoParams.algoType === OrderAlgoType.SNIPER) {
            let sniperDelay: number = algoParams.sniperDelay;
            const sniperDelayUnit: number = sniperDelay && getNearestSniperDelayUnit(sniperDelay);

            if (sniperDelayUnit) {
                sniperDelay = sniperDelay / sniperDelayUnit;
            }

            result = {
                sniperDelay,
                sniperDelayUnit
            };
        }

        return result;
    }

    private updateSecurityFlags(): void {
        if (this.security) {
            this.isSmartSecurity = isSmartSecurityKey(this.security);
            this.isAsecurity = isAsecurityKey(this.security);
        } else {
            this.isSmartSecurity = false;
            this.isAsecurity = false;
        }
    }

}
